// ---------------------------------------------------------------------------
// NumericOverviewSections — the Scale and Comparison cards for a numeric
// comparison grader. Read-only: the numeric spec is fixed at creation time, so
// this ignores the Overview edit context. Extracted from OverviewTab so the
// grader-type registry owns this markup.
// ---------------------------------------------------------------------------

import { Field, Input } from '@fluentui/react-components';

import { useStyles } from '../../GraderPage.style.js';
import type { OverviewSectionsProps } from '../graderType.types.js';

export function NumericOverviewSections({ judge }: OverviewSectionsProps) {
  const styles = useStyles();
  const numeric = judge.numeric;

  return (
    <>
      <section className={styles.overviewCard} aria-labelledby="grader-scale">
        <div id="grader-scale" className={styles.overviewCardTitle}>
          Scale
          <span className={styles.overviewCardTitleHelp}>
            Creation-time scoring scale and agreement tolerance.
          </span>
        </div>
        <div className={styles.overviewCardFields}>
          <div className={styles.overviewFieldRow}>
            <Field
              className={styles.overviewFieldColumn}
              label="Min scale"
              hint="Lowest score on the scale."
            >
              <Input
                className={styles.fullWidthControl}
                value={numeric?.minScale ?? ''}
                placeholder="e.g. 1"
                disabled
              />
            </Field>
            <Field
              className={styles.overviewFieldColumn}
              label="Max scale"
              hint="Highest score on the scale."
            >
              <Input
                className={styles.fullWidthControl}
                value={numeric?.maxScale ?? ''}
                placeholder="e.g. 5"
                disabled
              />
            </Field>
          </div>
          <div className={styles.overviewFieldRow}>
            <Field
              className={styles.overviewFieldColumn}
              label="Tolerance"
              hint="Within-tolerance match window (default 1)."
            >
              <Input
                className={styles.fullWidthControl}
                value={numeric?.tolerance ?? ''}
                placeholder="default (1)"
                disabled
              />
            </Field>
            <Field
              className={styles.overviewFieldColumn}
              label="Step"
              hint="Spacing between ordinal levels (default 1)."
            >
              <Input
                className={styles.fullWidthControl}
                value={numeric?.step ?? ''}
                placeholder="default (1)"
                disabled
              />
            </Field>
          </div>
        </div>
      </section>

      <section
        className={styles.overviewCard}
        aria-labelledby="grader-comparison"
      >
        <div id="grader-comparison" className={styles.overviewCardTitle}>
          Comparison
          <span className={styles.overviewCardTitleHelp}>
            Per-trial score expressions, resolved fresh for each row.
          </span>
        </div>
        <div className={styles.overviewCardFields}>
          <Field
            label="Expected score"
            hint="Golden / ground-truth score expression."
          >
            <Input
              className={styles.fullWidthControl}
              value={numeric?.scoreExpected ?? ''}
              placeholder="{{$expr:$.queryItem.data.expectedScore}}"
              disabled
            />
          </Field>
          <Field
            label="Actual score"
            hint="Predicted score expression from the judge under test."
          >
            <Input
              className={styles.fullWidthControl}
              value={numeric?.scoreActual ?? ''}
              placeholder="{{$expr:$.turn.output.score}}"
              disabled
            />
          </Field>
        </div>
      </section>
    </>
  );
}
